import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import SimulationResultsContent from '../simulationResults/SimulationResultsContent';
import ReadOnlyChatView, { ReadOnlyChatMessage } from '../digitalTwin/components/ReadOnlyChatView';

/**
 * One rating item, rendered by kind. A rating item is polymorphic: its ref is either a bare-integer
 * simulation id ("42") or a chat-session ref ("chat_9"). This is the single place the two diverge —
 * everything above it (the workbench, the rating bar, autosave) is item-agnostic.
 *
 *   - simulation  -> the product's own results view (SimulationResultsContent), which fetches the
 *                    simulation payload itself from the rating endpoint. Unchanged from before.
 *   - chat_<id>   -> a read-only transcript. The chat payload is small and chat-shaped (no
 *                    simulation_response), so SimulationResultsContent can't render it; fetch it
 *                    here and hand the messages to ReadOnlyChatView.
 *
 * `assignmentId` is the assignment (queue) id; `itemRef` is the item ref carried on the assignment.
 */

const API_URL = import.meta.env.VITE_API_URL || '';

const CHAT_PREFIX = 'chat_';

interface ChatPayload {
  kind: 'chat_session';
  id: number;
  title: string | null;
  messages: ReadOnlyChatMessage[];
}

interface RatingItemProps {
  /**
   * A stable numeric key + the id SimulationResultsContent fetches under when no explicit
   * ratingEndpointPath is given. Single rating passes the assignment id (and no path, so it uses
   * `/rlhf/assignments/{id}/simulation`); pairwise passes the pair id AND an explicit side path.
   */
  keyId: number;
  itemRef: string;
  /** Pairwise passes a side path; single rating leaves it undefined and uses the assignment path. */
  ratingEndpointPath?: string;
  /** Pairwise stacks the inputs above the report; single rating shows them side-by-side. */
  stackInputs?: boolean;
}

const RatingItem: React.FC<RatingItemProps> = ({
  keyId,
  itemRef,
  ratingEndpointPath,
  stackInputs,
}) => {
  const isChat = itemRef.startsWith(CHAT_PREFIX);

  if (!isChat) {
    // Simulation: delegate to the existing results renderer, which fetches on its own.
    return (
      <SimulationResultsContent
        key={keyId}
        simulationId={keyId}
        ratingEndpointPath={ratingEndpointPath}
        isRatingView
        stackInputs={stackInputs}
        hideChat
      />
    );
  }

  return <ChatRatingItem keyId={keyId} ratingEndpointPath={ratingEndpointPath} />;
};

const ChatRatingItem: React.FC<{ keyId: number; ratingEndpointPath?: string }> = ({
  keyId,
  ratingEndpointPath,
}) => {
  const [payload, setPayload] = useState<ChatPayload | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const path = ratingEndpointPath ?? `/rlhf/assignments/${keyId}/simulation`;
    (async () => {
      try {
        const res = await fetch(`${API_URL}${path}`, { credentials: 'include' });
        if (!res.ok) throw new Error(`Request failed (${res.status})`);
        const data = await res.json();
        if (!cancelled) setPayload(data as ChatPayload);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load conversation');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [keyId, ratingEndpointPath]);

  if (error) {
    return <div className="px-6 py-10 text-center text-sm text-red-600">{error}</div>;
  }
  if (!payload) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 text-[#07E5D1] animate-spin" />
      </div>
    );
  }
  return <ReadOnlyChatView messages={payload.messages} title={payload.title} />;
};

export default RatingItem;
